// "We've updated our terms" (fresh-start spec §3): the one prompt for an
// account that is missing an acceptance sign-up would have given — a version
// bump, an account from before, a Google sign-up, consents reset. It is
// driven by GET /consents/status: `missing` (the consents not held at their
// current version; Strava's only while Strava is linked) and
// `adult_confirmed`. One checkbox, then POST /consents/batch with the
// missing types and confirm_adult when 18+ is asked. It also opens whenever
// the server refuses for want of the AI consent (403 ai_consent_required, a
// coach message's Review consent: AI_CONSENT_EVENT). Blocking, like the
// consents it asks for: the way out is Sign out (or, for Strava's terms
// alone, Disconnect Strava). Never for a coach viewing an athlete.
//
// An onboarded athlete who confirms 18+ only now also starts their coach
// (POST /v2/onboarding/complete), as the cutover's "Your coach has moved"
// sheet did: without it the coach built them nothing (D58).
//
// Mounted in Layout and on the onboarding page. The rules are
// lib/aiConsent.js; the words are AiConsentCopy.jsx.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { accountsAPI, consentsAPI, v2API } from '../../services/api';
import { AI_CONSENT_EVENT, AI_COACHING_CONSENT, STRAVA_CONSENT_TYPE, consentsFor, termsToAccept } from '../../lib/aiConsent';
import { useAuth } from '../../context/AuthContext';
import { useV2 } from '../../context/V2Context';
import { newKey } from '../../v2/client';
import { formFailure } from '../../v2/failures';
import { getStarted } from '../../v2/startCoach';
import { Button } from '../ds';
import { CheckList, ModalActions, ModalError, ModalLead, ModalShell, ModalTitle } from '../appstate/ModalShell';
import { AcceptCheckbox, AiConsentText, PrivacyLink, STRAVA_TERMS, TermsLink } from './AiConsentCopy';

export const TERMS_TITLE = "We've updated our terms";

/** The modal for `need` ({ types, adult }, termsToAccept): the words of each consent asked for, and the one agreement. */
export function TermsView({ need, agreed, onAgreed, busy = null, error = null, onAccept, onSignOut, onDisconnectStrava }) {
  const ai = need.types.includes(AI_COACHING_CONSENT.type);
  const strava = need.types.includes(STRAVA_CONSENT_TYPE);
  const consents = need.types.length > 0;
  return (
    <ModalShell labelledBy="terms-title" z="z-[10003]">
      <ModalTitle id="terms-title">{TERMS_TITLE}</ModalTitle>
      <ModalLead>
        {consents
          ? 'Please read them and agree to carry on with your coach.'
          : "Aithlo coaching is for adults: please confirm you're 18 or over."}
      </ModalLead>
      {ai && <AiConsentText />}
      {strava && (
        <>
          <p className="m-0 type-label-section text-text-primary">Your Strava data</p>
          <CheckList items={STRAVA_TERMS} />
        </>
      )}
      {consents && (
        <p className="m-0 type-label">
          <TermsLink>Read the full Terms of Service</TermsLink> · <PrivacyLink />
        </p>
      )}
      <AcceptCheckbox checked={agreed} onChange={onAgreed} disabled={Boolean(busy)} consents={consents} adult={need.adult} />
      <ModalError>{error}</ModalError>
      <ModalActions>
        {strava && onDisconnectStrava && (
          <Button kind="text" size="small" onClick={onDisconnectStrava} disabled={Boolean(busy)}>
            {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect Strava'}
          </Button>
        )}
        {onSignOut && <Button kind="text" size="small" onClick={onSignOut} disabled={Boolean(busy)}>Sign out</Button>}
        <Button size="small" onClick={onAccept} disabled={!agreed || Boolean(busy)}>
          {busy === 'accept' ? 'Saving…' : 'Agree and continue'}
        </Button>
      </ModalActions>
    </ModalShell>
  );
}

export default function TermsModal({ viewing = false, onboarded = false }) {
  const { enabled, readOnly, confirmAdult } = useV2();
  const { logout } = useAuth() || {};
  const [need, setNeed] = useState(null);
  const [status, setStatus] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(null);         // 'accept' | 'disconnect' | null
  const [error, setError] = useState(null);
  const key = useRef(newKey());
  const recorded = useRef(false);                 // the batch went through; only the coach's start is left
  const off = viewing || readOnly;

  const check = useCallback(async ({ requireAi = false } = {}) => {
    if (off) return;
    try {
      const s = await consentsAPI.getStatus();
      setStatus(s);
      setNeed(termsToAccept(s, { requireAi }));
    } catch (err) {
      console.error('Failed to load consent status:', err);
      // The server refused for want of the AI consent: ask for it even without a status.
      if (requireAi) setNeed({ types: [AI_COACHING_CONSENT.type], adult: false });
    }
  }, [off]);

  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    const ask = () => check({ requireAi: true });
    window.addEventListener(AI_CONSENT_EVENT, ask);
    return () => window.removeEventListener(AI_CONSENT_EVENT, ask);
  }, [check]);

  if (off || !need) return null;

  const accept = async () => {
    if (!agreed || busy) return;
    setBusy('accept');
    setError(null);
    try {
      if (!recorded.current) {
        await consentsAPI.recordBatch(consentsFor(status, need.types), need.adult);
        recorded.current = true;
      }
      // Onboarded but never confirmed 18+: the coach starts now (onboarding
      // starts it itself). A failure here keeps the modal, and Agree tries
      // only this again: the acceptances are recorded.
      if (need.adult && onboarded && enabled) await getStarted(v2API, key.current);
      if (need.adult) confirmAdult?.();
      recorded.current = false;
      setAgreed(false);
      setNeed(null);
    } catch (err) {
      console.error('Failed to record the acceptance:', err);
      setError(recorded.current ? formFailure('onboarding', err).text : "Couldn't save that — check your connection and try again.");
    } finally {
      setBusy(null);
    }
  };

  // Strava's terms declined: disconnecting deletes what we hold, and nothing else is asked of Strava.
  const disconnect = async () => {
    if (busy) return;
    setBusy('disconnect');
    setError(null);
    try {
      await accountsAPI.disconnectStrava();
      const rest = { ...need, types: need.types.filter((t) => t !== STRAVA_CONSENT_TYPE) };
      setNeed(rest.types.length || rest.adult ? rest : null);
    } catch (err) {
      console.error('Failed to disconnect Strava:', err);
      setError("Couldn't disconnect Strava just now — try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <TermsView need={need} agreed={agreed} onAgreed={setAgreed} busy={busy} error={error}
      onAccept={accept} onSignOut={logout} onDisconnectStrava={disconnect} />
  );
}
